/**
 * livretToTrameSync.js — Sync inverse : Livret Plateau (ch06 progression)
 * → Auto-évaluation de la Trame Plateau P1/P2/P3.
 *
 * Cas d'usage : L'élève coche « acquis » sur une compétence dans son livret
 * interactif (ch06 « Ma progression »). Au moment de préparer sa prochaine
 * séance sur la trame, il peut cliquer « Pré-remplir depuis mon livret »
 * → toutes les auto-évals correspondantes passent à « ✅ ok ».
 *
 * Règles clefs :
 *   • On ne modifie JAMAIS un exercice déjà à « ok » (pour ne pas régresser).
 *   • On ne touche pas aux exercices que le livret n'a pas ; le mapping est
 *     l'inverse de PLATEAU_AUTOEVAL_TO_PROG (index prog → liste exerciceIds).
 *   • Toutes les phases sont mises à jour d'un coup (P1, P2, P3) car un même
 *     exercice peut appartenir à une phase mais pas une autre.
 *
 * Retour : { updated, skipped, phases } — utilisé par la UI pour un toast clair.
 */

import { PLATEAU_AUTOEVAL_TO_PROG, autoEvalToLivretStatus } from './livretAutoEvalMap';

/** Inverse : indexProgression (livret ch06) → liste des exerciceIds trame. */
export const PROG_TO_PLATEAU_AUTOEVAL = (() => {
  const inv = {};
  Object.entries(PLATEAU_AUTOEVAL_TO_PROG).forEach(([exId, progIdx]) => {
    if (!inv[progIdx]) inv[progIdx] = [];
    inv[progIdx].push(exId);
  });
  return inv;
})();

/** Convertit un statut livret vers une valeur d'auto-éval trame. */
export function livretStatusToAutoEval(status) {
  if (status === 'acquis')    return 'ok';
  if (status === 'encours')   return 'todo';
  if (status === 'decouvert') return 'no';
  return null;
}

/**
 * Lit l'état du livret Plateau (progression chapitre 06) depuis localStorage.
 * Le livret Plateau stocke les clés sous la forme `prog-<N>` (0 à 11).
 *
 * @param {string} storageKey — clé de stockage du livret (par défaut « code2roues:livret-plateau »).
 * @returns {Object} { 'prog-N': 'acquis'|'encours'|'decouvert' }
 */
export function loadLivretProgress(storageKey = 'code2roues:livret-plateau') {
  if (typeof window === 'undefined') return {};
  try {
    const raw = JSON.parse(localStorage.getItem(storageKey) || '{}');
    const result = {};
    Object.entries(raw).forEach(([k, v]) => {
      if (k.startsWith('prog-') && (v === 'acquis' || v === 'encours' || v === 'decouvert')) {
        result[k] = v;
      }
    });
    return result;
  } catch (_) {
    return {};
  }
}

/**
 * Applique les compétences acquises/en cours du livret sur les auto-évals
 * de toutes les phases Plateau détectées dans localStorage.
 *
 * @param {Object} progressDict — { 'prog-N': 'acquis'|'encours'|'decouvert' }
 * @param {Object} opts
 *   - onlyAcquired (bool, défaut true) : ne pré-remplit que les compétences « acquises »
 *     (évite de dégrader une auto-éval déjà positive avec des « encours » du livret)
 *   - overwriteOk (bool, défaut false) : si false, ne modifie pas un exercice déjà en « ok »
 *
 * @returns {Object} { updated: number, skipped: number, phases: string[], details: [] }
 */
export function applyLivretToPlateauAutoEval(progressDict, opts = {}) {
  const { onlyAcquired = true, overwriteOk = false } = opts;
  const summary = { updated: 0, skipped: 0, phases: new Set(), details: [] };

  if (typeof window === 'undefined') return { ...summary, phases: [] };

  // 1) Construire la liste des exerciceIds à sync + leur valeur cible
  const targets = {}; // { exerciseId: 'ok'|'todo'|'no' }
  Object.entries(progressDict || {}).forEach(([progKey, status]) => {
    if (onlyAcquired && status !== 'acquis') return;
    const val = livretStatusToAutoEval(status);
    if (!val) return;
    const idx = progKey.replace('prog-', '');
    const exIds = PROG_TO_PLATEAU_AUTOEVAL[idx] || [];
    exIds.forEach((exId) => {
      targets[exId] = val;
    });
  });

  if (Object.keys(targets).length === 0) return { ...summary, phases: [] };

  // 2) Parcourir toutes les clés d'auto-éval Plateau et merger
  const prefix = 'code2roues:autoeval-plateau-';
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(prefix) || !key.endsWith(':v1')) continue;
      let data = {};
      try { data = JSON.parse(localStorage.getItem(key) || '{}'); } catch (_) { continue; }

      let touched = false;
      Object.entries(targets).forEach(([exId, val]) => {
        // On n'ajoute que si l'exercice est référencé dans cette phase (déjà présent avec une valeur)
        // OU s'il n'existe pas encore mais qu'on veut le pré-remplir (comportement défaut : ajout)
        const existing = data[exId];
        if (existing === val) return;
        if (!overwriteOk && existing === 'ok') {
          summary.skipped += 1;
          return;
        }
        data[exId] = val;
        touched = true;
        summary.updated += 1;
        summary.details.push({ phase: key, exId, from: existing || null, to: val });
      });

      if (touched) {
        localStorage.setItem(key, JSON.stringify(data));
        summary.phases.add(key.replace(prefix, '').replace(':v1', ''));
      }
    }
  } catch (_) { /* localStorage inaccessible */ }

  return { ...summary, phases: Array.from(summary.phases) };
}

/**
 * Utilitaire de comptage : combien d'exercices SERAIENT sync (dry-run)
 * — utilisé pour afficher un CTA "Sync X exercices" AVANT le clic.
 */
export function previewLivretToTrameSync(progressDict) {
  const targets = {};
  Object.entries(progressDict || {}).forEach(([progKey, status]) => {
    if (status !== 'acquis') return;
    const val = livretStatusToAutoEval(status);
    if (!val) return;
    const idx = progKey.replace('prog-', '');
    (PROG_TO_PLATEAU_AUTOEVAL[idx] || []).forEach((exId) => {
      targets[exId] = val;
    });
  });
  return { acquiredCount: Object.keys(targets).length, exerciseIds: Object.keys(targets) };
}
